/**
 * Order types for persistent survey storage.
 */
import { z } from 'zod';

/**
 * Order API error response type.
 * Used for consistent error responses across order endpoints.
 */
export interface OrderApiError {
  error: string;
  details?: unknown;
}

/**
 * Possible order status values.
 * - INIT: Order created but no survey data saved yet
 * - STARTED: First survey question has been answered
 * - PAYMENT_PENDING: Checkout session created, awaiting payment
 * - PAYMENT_COMPLETED: Payment received via webhook
 * - PAYMENT_FAILED: Payment failed or session expired
 * - GENERATING: Story generation in progress
 * - PLAN_REVIEW: Waiting for human approval of story plan
 * - CHARACTER_SHEET_REVIEW: Waiting for human approval of character sheet
 * - GENERATING_STORYBOARD: Storyboard generation in progress. Since #916 this
 *   step is AI-gated only and advances straight to GENERATING_FINAL via
 *   STORYBOARD_DONE — there is no human storyboard review any more
 * - STORYBOARD_REVIEW: DEAD since #916. No producer and no transitions — it
 *   has no node in the lifecycle machine, so `getNextStatus` returns null for
 *   every event; kept only because the pgEnum value cannot be dropped and
 *   legacy rows must still parse
 * - GENERATION_COMPLETE: Generation pipeline finished successfully
 * - GENERATION_FAILED: Generation pipeline encountered an error
 * - PREVIEW_READY: Preview available for customer
 * - DRAFT_READY: Mastra workflow draft delivered — plan and character sheet
 *   approved, pages not yet generated (increment one's terminal status)
 * - CANCELLED: User requested to start over
 * - PRINT_JOB_SUBMITTED: Print job sent to Lulu
 * - SHIPPED: Lulu reports shipment with tracking
 */
export const ORDER_STATUS = [
  'INIT',
  'STARTED',
  'PAYMENT_PENDING',
  'PAYMENT_COMPLETED',
  'PAYMENT_FAILED',
  'GENERATING',
  'GENERATING_PLAN',
  'GENERATING_CHARACTER_SHEET',
  'GENERATING_STORYBOARD',
  'GENERATING_FINAL',
  'PLAN_REVIEW',
  'CHARACTER_SHEET_REVIEW',
  // DEAD since #916 — no producer, no transitions; retained because the pgEnum value cannot be dropped and legacy rows must still parse
  'STORYBOARD_REVIEW',
  'GENERATION_COMPLETE',
  'GENERATION_FAILED',
  'PREVIEW_READY',
  'DRAFT_READY',
  'CANCELLED',
  'PRINT_JOB_SUBMITTED',
  'SHIPPED',
] as const;
export type OrderStatus = (typeof ORDER_STATUS)[number];

/**
 * Task 26 fixup (orchestrator review) — the production phase
 * `apps/server/src/book/productionPhase.ts`'s `deriveProductionPhase`
 * projects from durable server state (the active candidate revision's
 * manifest + ledger acceptance) while an order sits at `GENERATING_FINAL`.
 * Four buckets, not the full script/prose/storyboard/pages/cover/
 * continuity/PDF chain — see that module's own docblock for why storyboard
 * and continuity/PDF each collapse into a neighbour rather than getting
 * their own customer-visible slug. A closed, small, deliberately-designed
 * enum (like `regenerationScopeSchema`), not the "prefer `z.string()`" rule
 * for open-ended server vocabulary — this is a fixed state machine the
 * client renders copy for, not a growing set of slugs.
 */
export const PRODUCTION_PHASE_SLUGS = [
  'writing',
  'pages',
  'cover',
  'finishing',
] as const;
export type ProductionPhaseSlug = (typeof PRODUCTION_PHASE_SLUGS)[number];

export const productionPhaseDetailSchema = z.object({
  phase: z.enum(PRODUCTION_PHASE_SLUGS),
  /** Only populated once the script (and with it the page count) is known. */
  pagesAccepted: z.number().int().nonnegative().optional(),
  pagesTotal: z.number().int().positive().optional(),
});

export type ProductionPhaseDetail = z.infer<typeof productionPhaseDetailSchema>;

/**
 * Public order data (excludes secret).
 */
export const orderPublicSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email().nullable(),
  surveyJson: z.record(z.string(), z.unknown()).nullable(),
  status: z.enum(ORDER_STATUS),
  paidProductType: z.string().nullable().optional(),
  appEnv: z.string().nullable().optional(),
  aiCostCents: z.number().int().nullable().optional(),
  luluPrintCostCents: z.number().int().nullable().optional(),
  luluShipCostCents: z.number().int().nullable().optional(),
  /** Current Mastra `bookWorkflow` run id (`orders.workflow_run_id`), or null before the first run. */
  workflowRunId: z.string().nullable(),
  /** Three-way failure category from `book/failures.ts`, or null when there is no failure on record. */
  failureCategory: z.string().nullable(),
  /**
   * Populated ONLY while `status === 'GENERATING_FINAL'` (the server omits
   * it, rather than nulling it, the rest of the time — cheap on every other
   * read). See {@link ProductionPhaseDetail}.
   */
  productionPhase: productionPhaseDetailSchema.nullable().optional(),
  /**
   * The review revision (a `reviewPlan` / `reviewCharacterSheet` suspend
   * payload's `revision`) whose decision the server last APPLIED
   * (`orders.last_submitted_review_revision`), or null. A review card whose
   * revision equals this is already decided, even while a stale workflow
   * snapshot still reads it as suspended. Optional on the wire so an older
   * server that does not send it still parses.
   */
  lastSubmittedReviewRevision: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type OrderPublic = z.infer<typeof orderPublicSchema>;

/**
 * The machine-readable `reason` on the server's 409 `REVIEW_NOT_PENDING`
 * body (`apps/server/src/book/ownershipGate.ts`) when a review resume
 * replays the revision the server already durably APPLIED — the
 * {@link OrderPublic.lastSubmittedReviewRevision} column. Unlike every other
 * resume refusal this one is PERMANENT, so a client may treat the review as
 * consumed instead of re-reading and retrying. Every other 409 carries no
 * `reason`.
 */
export const REVIEW_REVISION_APPLIED_REASON = 'review_revision_applied';

/**
 * How long a review revision whose resume was ACKNOWLEDGED (a 2xx from
 * Mastra's fire-and-forget `/resume`) stays treated as submitted before the
 * durable {@link OrderPublic.lastSubmittedReviewRevision} column alone
 * decides. Shared so the server's in-memory replay hold
 * (`apps/server/src/book/ownershipGate.ts`) and the client's own
 * acknowledgement memory (`useBookRun.ts`) expire together: a resume that was
 * acknowledged but never applied becomes resumable — and visible — again at
 * the same moment on both sides.
 */
export const CONSUMED_REVISION_HOLD_MS = 10 * 60 * 1000;

/**
 * Response from POST /api/orders/init
 */
export const initOrderResponseSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(ORDER_STATUS),
  /** Cookie max age in milliseconds - client should track expiration */
  maxAgeMs: z.number(),
  /**
   * Session ID returned in response body as a cross-origin fallback.
   * In cross-origin deployments (e.g. Vercel frontend + Render backend),
   * the session_id cookie set via Set-Cookie is stored on the API domain
   * and is NOT readable via document.cookie on the frontend domain.
   * The client uses this field to populate localStorage so that the
   * session persists across page navigations (e.g. Stripe redirect).
   */
  sessionId: z.string().uuid(),
});

export type InitOrderResponse = z.infer<typeof initOrderResponseSchema>;

/**
 * Request body for PUT /api/orders/save/:id
 */
export const saveOrderRequestSchema = z.object({
  surveyJson: z.record(z.string(), z.unknown()),
  email: z.string().email().optional(),
});

export type SaveOrderRequest = z.infer<typeof saveOrderRequestSchema>;

/**
 * Response from PUT /api/orders/save/:id
 */
export const saveOrderResponseSchema = z.object({
  id: z.string().uuid(),
  status: z.enum(ORDER_STATUS),
  surveyJson: z.record(z.string(), z.unknown()).nullable(),
});

export type SaveOrderResponse = z.infer<typeof saveOrderResponseSchema>;

/**
 * Response from PUT /api/orders/cancel/:id
 */
export const cancelOrderResponseSchema = z.object({
  id: z.string().uuid(),
  status: z.literal('CANCELLED'),
});

export type CancelOrderResponse = z.infer<typeof cancelOrderResponseSchema>;

// ============================================================================
// Payment Types
// ============================================================================

/**
 * Stripe address from checkout session
 */
export interface StripeAddress {
  city: string | null;
  country: string | null;
  line1: string | null;
  line2: string | null;
  postal_code: string | null;
  state: string | null;
  name?: string | null;
}

/**
 * Response from POST /api/orders/:id/checkout-session
 */
export const checkoutSessionResponseSchema = z.object({
  clientSecret: z.string(),
});

export type CheckoutSessionResponse = z.infer<
  typeof checkoutSessionResponseSchema
>;

/**
 * Response from GET /api/orders/:id/payment-status
 */
export const paymentStatusResponseSchema = z.object({
  status: z.enum(ORDER_STATUS),
  paymentStatus: z.string().nullable(),
  isPaymentComplete: z.boolean(),
  email: z.string().email().nullable().optional(),
  phone: z.string().nullable().optional(),
  customerName: z.string().nullable().optional(),
  amountTotal: z.number().nullable().optional(),
});

export type PaymentStatusResponse = z.infer<typeof paymentStatusResponseSchema>;

/**
 * Check if an order status indicates payment has been completed.
 * Statuses after PAYMENT_COMPLETED all imply the order has been paid for.
 */
export function isPaymentCompleteFromStatus(status: OrderStatus): boolean {
  return (
    [
      'PAYMENT_COMPLETED',
      'GENERATING',
      'GENERATING_PLAN',
      'GENERATING_CHARACTER_SHEET',
      'GENERATING_STORYBOARD',
      'GENERATING_FINAL',
      'PLAN_REVIEW',
      'CHARACTER_SHEET_REVIEW',
      'STORYBOARD_REVIEW',
      'GENERATION_COMPLETE',
      'GENERATION_FAILED',
      'DRAFT_READY',
      'PREVIEW_READY',
      'PRINT_JOB_SUBMITTED',
      'SHIPPED',
    ] as readonly string[]
  ).includes(status);
}

// ============================================================================
// Session Types
// ============================================================================

/**
 * Response from GET /api/sessions/active-order
 * Tells the client what to do with an existing session.
 */
export interface ActiveOrderResponse {
  action: 'redirect_finalize' | 'restore_draft' | 'create_new';
  orderId?: string;
  orderStatus?: OrderStatus;
}

/**
 * Response from PUT /api/orders/reset/:id
 * Confirms the order was reset to PAYMENT_COMPLETED.
 */
export interface ResetOrderResponse {
  id: string;
  status: 'PAYMENT_COMPLETED';
}

/**
 * Request body for POST /api/sessions/recover
 */
export interface SessionRecoverRequest {
  email: string;
}

/**
 * Request body for POST /api/sessions/verify-code
 */
export interface SessionVerifyCodeRequest {
  email: string;
  code: string;
}
